/**
 * Recovering a tab whose code was deleted underneath it.
 *
 * Every route below the entry chunk is a dynamic `import()`, and Vite names the
 * files it produces after a hash of their contents. A deploy therefore replaces
 * `DashboardShell-Cq-33Ysp.js` with a differently named file and removes the old
 * one — so a tab that has been open across a deploy asks for a name no pod has,
 * gets a 404, and paints the error card at the moment the citizen taps a link.
 * The web pods roll two at a time as well, which opens the same hole for a few
 * seconds even for a tab loaded during the rollout: a fresh `index.html` from
 * the new pod naming chunks the old pod has not got.
 *
 * `UpdateBanner` in `version.tsx` is the polite half of this and stays: it polls
 * `/version.json`, notices a new build and offers a reload before anything
 * breaks. This is the half for when nothing was noticed in time. The two answer
 * different questions — "there is something newer" against "what this tab is
 * running no longer exists" — and only the second one justifies reloading a page
 * the citizen did not ask to reload.
 *
 * Retaining old chunks on a CDN (see `infra/aws/cdn/README.md`) is the real fix
 * and removes most of these. This stays regardless: it costs nothing, it covers
 * the tabs already open on the deploy that turns the CDN on, and a bundle older
 * than the CDN's retention window is still a bundle whose files are gone.
 */

/** Set before reloading and cleared once the fresh page has proved it lives. */
const RELOAD_FLAG = 'servicesai:staleChunkReload';

/**
 * How long a reloaded page must survive before it is trusted.
 *
 * The gate exists so that a chunk which 404s for some reason a reload cannot fix
 * — a half-published deploy, a broken asset host — reloads once and then shows
 * the error card, rather than spinning the page forever. Clearing the flag after
 * a stretch of ordinary use is what stops that gate from being permanent: the
 * next deploy, hours later, gets its own single attempt.
 */
const RECOVERY_WINDOW_MS = 10_000;

/**
 * How a browser says the file is not there. Every engine words it differently
 * and each is a whole-message match against the same event, so all of them are
 * here; a missed one is a citizen looking at the error card.
 */
const STALE_CHUNK_MESSAGES = [
  // Chromium
  'failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // WebKit
  'importing a module script failed',
  // Vite's own preload helper, for a stylesheet belonging to a missing route
  'unable to preload css',
];

/** What the gate needs from the browser, so a test can hand it something else. */
export interface StaleChunkEnvironment {
  /**
   * Per-tab on purpose. A second tab recovering from the same deploy has its own
   * attempt, and neither spends the other's.
   */
  readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  readonly online: boolean;
  readonly reload: () => void;
}

function browserEnvironment(): StaleChunkEnvironment {
  return {
    storage: readSessionStorage(),
    // `false` is the only trustworthy answer here — `true` means "there is an
    // interface", not "the network works" — and it is the one worth acting on: a
    // dynamic import also fails when the citizen walks into a lift, and
    // reloading then replaces a working page with the browser's offline screen.
    online: typeof navigator === 'undefined' || navigator.onLine !== false,
    reload: () => window.location.reload(),
  };
}

function readSessionStorage(): StaleChunkEnvironment['storage'] {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Private browsing, or storage disabled by policy.
    return null;
  }
}

/**
 * Whether this is a route whose file has gone, rather than a fault in the code.
 *
 * Deliberately a message match. There is no error type to check: the rejection a
 * failed dynamic import produces is a plain `TypeError`, the same class an
 * ordinary programming mistake throws, so the wording is the only thing that
 * separates "this build is stale" from "this component is broken" — and
 * reloading on the second would hide a real bug behind a refresh loop.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name}: ${error.message}`
      : typeof error === 'string'
        ? error
        : '';
  if (!message) return false;
  const lower = message.toLowerCase();
  return STALE_CHUNK_MESSAGES.some((phrase) => lower.includes(phrase));
}

/**
 * Reload once for a chunk that is no longer deployed. Returns whether a reload
 * was started, so a caller can fall back to showing something when it was not.
 */
export function reloadForStaleChunk(env: StaleChunkEnvironment = browserEnvironment()): boolean {
  if (!env.online) return false;

  // No storage means no gate, and a reload loop is worse than an error card.
  if (!env.storage) return false;
  try {
    if (env.storage.getItem(RELOAD_FLAG)) return false;
    env.storage.setItem(RELOAD_FLAG, '1');
  } catch {
    return false;
  }

  env.reload();
  return true;
}

/**
 * Listen for a route that failed to load and recover the tab.
 *
 * Call once per app, above the root render. The error boundaries catch the same
 * failure when it happens during a render; this catches the rest — a preload, a
 * router prefetch, an `import()` inside an event handler — none of which reach a
 * boundary at all, and all of which otherwise leave the citizen tapping a link
 * that does nothing.
 *
 * Returns a teardown, which nothing in an app entry uses and every test does.
 */
export function installStaleChunkReload(
  env: StaleChunkEnvironment = browserEnvironment(),
): () => void {
  if (typeof window === 'undefined') return () => {};

  const onPreloadError = (event: Event) => {
    // Vite dispatches this for a failed `__vitePreload`, carrying the original
    // error as `payload`.
    const payload = (event as Event & { payload?: unknown }).payload;
    if (isStaleChunkError(payload)) reloadForStaleChunk(env);
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (isStaleChunkError(event.reason)) reloadForStaleChunk(env);
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onRejection);

  // A page that has been usable for this long is not the page that failed, so
  // the next deploy gets an attempt of its own.
  const settled = setTimeout(() => {
    try {
      env.storage?.removeItem(RELOAD_FLAG);
    } catch {
      // Nothing to clear if storage went away; the gate simply stays closed.
    }
  }, RECOVERY_WINDOW_MS);

  return () => {
    clearTimeout(settled);
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
